import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StackNavigationProp } from '@react-navigation/stack'
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native'
import { View, ScrollView, Text } from 'react-native'
import { paths } from '../../api/pathsApi'
import { AlertMessage } from '../../components/AlertMessage'
import { FloatingBottomButtons } from '../../components/FloatingBottom/FloatingBottom'
import { TemplatePage } from '../../components/TemplatePage'
import { InitialDataFreshWater } from '../../context/FreshWater/types'
import { useForm } from '../../hooks/useForm'
import { useGetInitialDataByArea } from '../../hooks/useGetInitialDataByArea'
import { usePost } from '../../hooks/usePost'
import { globalStyle } from '../../theme/global'
import { PiscicultureCenter } from '../../components/PiscicultureCenter/PiscicultureCenter'
import { RoomsModule } from '../../components/RoomsModule/RoomsModule'
import { Ponds } from './components/Ponds'
import { Cages } from './components/Cages'
import { InitialDataResponse } from '../../context/SeaWater/types'
import { StateRecord } from '../../context/Alerts/types'
import { useSendNotifications } from '../../hooks/useSendNotifications'
import { Alert401 } from '../../components/AlertMessage/Alert401'
import { useContextApp } from '../../hooks/useContextApp'
import { RootParams, StatesValues } from '../../navigator/types'
import { textColor } from '../../theme/colors'

export interface SelectedState {
  [key: number]: InfoState
}
export interface InfoState {
  levelThreeId: number
  stateId: number
  levelThreeName: string
  currentState: string
  newState: string
}

interface NavigationParams extends StackNavigationProp<RootParams, 'States'> {}

export const States = () => {
  const { t } = useTranslation()

  const {
    user: { areaSelected },
    handleLogOut,
    handleSaveRecords,
    getTotalPendingRecords,
    freshWaterContext,
    seaWaterContext,
  } = useContextApp()

  useSendNotifications({ pendingRecords: getTotalPendingRecords(areaSelected!) })

  const registerKey = useRef('')

  const navigation = useNavigation<NavigationParams>()
  const [showAlert, setShoeAlert] = useState(false)
  const [error401, setError401] = useState<{ code: number | null; msg: string }>({
    code: null,
    msg: '',
  })

  const route = useRoute<RouteProp<RootParams, 'States'>>()

  const { values, onChangeValues } = useForm<Omit<StatesValues, 'items'>>({
    levelOneId: route.params?.levelOneId ?? 0,
    levelTwoId: route.params?.levelTwoId ?? 0,
    levelThreeName: route.params?.levelThreeName ?? '',
    currentState: route.params?.currentState ?? '',
    newState: route.params?.newState ?? '',
  })

  const [selectedStates, setSelectedState] = useState<SelectedState>({})
  const freshWaterData = useGetInitialDataByArea<InitialDataFreshWater>({
    url: paths().SYNC.INITIAL_FRESH,
    enabled: false,
    onSuccess(data) {
      navigation.replace('SuccessScreen')
      freshWaterContext.handleInittialData(data)
    },
  })
  const seaWaterData = useGetInitialDataByArea<InitialDataResponse>({
    url: paths().SYNC.INITIAL_SEA,
    enabled: false,
    onSuccess(data) {
      navigation.replace('SuccessScreen')
      seaWaterContext.handleInittialData(data)
    },
  })

  const onChangeState = useCallback((values: InfoState) => {
    setSelectedState((oldSelectedState) => {
      if (values.stateId === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [values.levelThreeId]: _, ...rest } = oldSelectedState
        return {
          ...rest,
        }
      }
      return {
        ...oldSelectedState,
        [values.levelThreeId]: values,
      }
    })
  }, [])

  const { handleSendData, isLoading, msgError } = usePost({
    url: areaSelected === 'sweetWater' ? paths().STATES.CHANGE_FRESH : paths().STATES.CHANGE_SEA,
    onSuccess() {
      if (areaSelected === 'sweetWater') {
        freshWaterData.getInitialData()
      } else {
        seaWaterData.getInitialData()
      }
    },
    onError(error) {
      if (error.code === 401) {
        handleSaveRecord()
        if (areaSelected === 'sweetWater') {
          freshWaterContext.handleResetData()
        } else {
          seaWaterContext.handleResetData()
        }
        setError401(error)
      }
    },
  })

  useEffect(() => {
    registerKey.current = `${values.levelOneId}-${values.levelTwoId}`
  }, [values])

  const handleSaveRecord = () => {
    const milliseconds = Date.now()
    const currentDate = new Date(milliseconds)
    handleSaveRecords<StateRecord[]>({
      record: Object.values(selectedStates).map((state: InfoState) => ({
        ...state,
        levelOneId: values.levelOneId,
        dateTime: currentDate.toUTCString(),
        levelTwoId: values.levelTwoId,
        parentKey: `${registerKey.current}`,
        registerKey: `${registerKey.current}-${state.levelThreeId}`,
      })),
      typeAlert: 'statesRecords',
      key: `${registerKey.current}`,
      area: areaSelected,
    })
  }
  return (
    <>
      <TemplatePage
        styleContent={{ padding: 0, marginBottom: 0 }}
        header
        title={`${t('Formulario Estados')}`}
      >
        <View style={globalStyle.viewContainer}>
          <ScrollView showsVerticalScrollIndicator={false}>
            <PiscicultureCenter
              initialValue={values.levelOneId}
              onChange={(value: number) => onChangeValues({ value, name: 'levelOneId' })}
            />
            {values.levelOneId !== 0 && (
              <RoomsModule
                parentId={values.levelOneId}
                onChange={(value: number) => onChangeValues({ value, name: 'levelTwoId' })}
                initialValue={values.levelTwoId}
              />
            )}
            {areaSelected === 'sweetWater' && (
              <>
                {values.levelTwoId !== 0 && (
                  <Ponds
                    roomId={values.levelTwoId}
                    getValues={onChangeState}
                    initValues={route.params?.items}
                  />
                )}
              </>
            )}

            {areaSelected === 'seaWater' && (
              <>
                {values.levelTwoId !== 0 && (
                  <Cages
                    moduleId={values.levelTwoId}
                    getValues={onChangeState}
                    initValues={route.params?.items}
                  />
                )}
              </>
            )}
          </ScrollView>
        </View>
      </TemplatePage>
      <Alert401 isRender={error401.code === 401} action={handleLogOut} />

      {(msgError.length > 0 || freshWaterData.msgError.length > 0) && (
        <AlertMessage title='ERROR'>
          <Text style={{ textAlign: 'center', color: textColor }}>{msgError}</Text>
        </AlertMessage>
      )}
      {showAlert && (
        <AlertMessage
          title={t('Guardado')}
          action={() => {
            setShoeAlert(false)
          }}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>
            {t('El registro se guardó con éxito')}
          </Text>
        </AlertMessage>
      )}
      {Object.values(selectedStates).length > 0 && (
        <FloatingBottomButtons
          isLoading={isLoading || freshWaterData.isLoading || seaWaterData.isLoading}
          onPressSave={() => {
            handleSaveRecord()
            setShoeAlert(true)
          }}
          onPressSend={() => {
            const keyName = areaSelected === 'seaWater' ? 'cageId' : 'tankId'

            const listSend = Object.values(selectedStates).map((item) => ({
              [keyName]: item.levelThreeId,
              stateId: item.stateId,
            }))

            handleSendData(listSend)
          }}
        />
      )}
    </>
  )
}
